import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import { formatDateForDisplay } from '../../utils/formatters';

export interface PatientStatusChipProps {
  /** PHOX `patients.status`. `active`, `null` and `undefined` all render nothing. */
  status?: string | null;
  /** `deceased | no_longer_a_patient | other` — used to word the tooltip. */
  reason?: string | null;
  /** ISO timestamp the status was last changed, for "since <date>" in the tooltip. */
  changedAt?: string | null;
}

const chipSx = {
  height: 20,
  fontSize: '0.7rem',
  fontWeight: 600,
  '& .MuiChip-label': { px: 0.75 },
} as const;

/** "no_longer_a_patient" -> "No longer a patient". Exported for
 * `PatientFlagsCard` (PRX-80 Task 7), the one other place that needs the
 * PRX-36 status reason worded as plain text rather than inside this chip's
 * tooltip — one transform, not a second copy of the dialog's `REASONS` labels
 * (`PatientStatusDialog.tsx`, which the server's own reason codes back). */
export function reasonSentence(reason?: string | null): string | null {
  if (!reason) return null;
  const words = reason.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function buildTooltip(fallback: string, reason?: string | null, changedAt?: string | null): string {
  const base = reasonSentence(reason) ?? fallback;
  if (!changedAt) return base;
  // `statusChangedAt` is a real instant (timestamptz), not a date-only field.
  // Handed to formatDateForDisplay as a STRING it is split on "T" — the UTC
  // day — so a status set after 8pm Eastern reads as tomorrow. Converting
  // through Date first gives the pharmacy's own day (GP-1294, same fix).
  return `${base} since ${formatDateForDisplay(new Date(changedAt))}`;
}

/**
 * PRX-36 — the PHOX-owned patient status (`patients.status`), set from
 * Patient 360's "Set status" action (D2's `deletePatients` permission).
 *
 * A SIBLING of `PatientKrollStatusChips`, not a merge with it: that component
 * shows what Kroll says about the patient's file; this one shows what PHOX
 * itself recorded, which is manual-only and never synced from a PMS (R1). The
 * two can legitimately disagree — a patient closed in Kroll can still be
 * `active` in PHOX, and vice versa — so both render, separately.
 *
 * Renders NOTHING for `active`, `null` or `undefined` (a search row or a
 * profile that never asked for the field) — an absent chip means exactly what
 * it means beside the Kroll chips: no unusual state, not "checked and fine".
 *
 * D3: `transferred` reads exactly like `inactive` here — it is never offered
 * in the status dialog, but a row can still carry it (imported/legacy data),
 * and it blocks new workflows the same way.
 */
export const PatientStatusChip: React.FC<PatientStatusChipProps> = ({
  status,
  reason,
  changedAt,
}) => {
  if (!status || status === 'active') return null;

  if (status === 'deceased') {
    const tooltip = buildTooltip('Deceased', reason, changedAt);
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          component="span"
          icon={<HeartBrokenIcon sx={{ fontSize: 14 }} />}
          label="Deceased"
          size="small"
          color="error"
          variant="outlined"
          aria-label={tooltip}
          sx={chipSx}
        />
      </Tooltip>
    );
  }

  if (status === 'inactive' || status === 'transferred') {
    const tooltip = buildTooltip('Inactive', reason, changedAt);
    return (
      <Tooltip title={tooltip} arrow>
        <Chip
          component="span"
          icon={<BlockIcon sx={{ fontSize: 14 }} />}
          label="Inactive"
          size="small"
          color="warning"
          variant="outlined"
          aria-label={tooltip}
          sx={chipSx}
        />
      </Tooltip>
    );
  }

  return null;
};

export default PatientStatusChip;
